import { defineStore } from "pinia";
import { useVideoStateStore } from "./VideoStateStore";
import type { IDeviceRepository } from "~/interfaces/IDeviceRepository";
import { DeviceRepository } from "~/repositories/DeviceRepository";

/**
 * 配信デバイスをリストアップ、選択デバイスを保持するstore
 */
export const useDeviceStore = defineStore("DeviceStore", () => {
  // ref: https://api-ref.agora.io/en/video-sdk/web/4.x/interfaces/iagorartc.html#getcameras
  // ref: https://api-ref.agora.io/en/video-sdk/web/4.x/interfaces/iagorartc.html#getmicrophones

  // interface MediaDeviceInfo {
  //     /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/MediaDeviceInfo/deviceId) */
  //     readonly deviceId: string;
  //     /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/MediaDeviceInfo/groupId) */
  //     readonly groupId: string;
  //     /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/MediaDeviceInfo/kind) */
  //     readonly kind: MediaDeviceKind;
  //     /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/MediaDeviceInfo/label) */
  //     readonly label: string;
  //     /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/MediaDeviceInfo/toJSON) */
  //     toJSON(): any;
  // }

  const repository: IDeviceRepository = new DeviceRepository();
  const videoStateStore = useVideoStateStore();

  // 選択しているカメラ
  const selectedCamera = ref<undefined | MediaDeviceInfo>(undefined);
  // カメラ一覧
  const cameras = ref<MediaDeviceInfo[]>([]);
  // カメラ一覧の名称配列
  const cameraLabels = ref<string[]>([]);

  // 選択しているマイク
  const selectedMicrophone = ref<undefined | MediaDeviceInfo>(undefined);
  // マイク一覧
  const microphones = ref<MediaDeviceInfo[]>([]);
  // マイク一覧の名称配列
  const microphoneLabels = ref<string[]>([]);

  // デバイス取得済みかどうか
  const fetchedDevices = ref<boolean>(false);

  /**
   * カメラ、マイク一覧を取得する
   * @param repository
   */
  const fetchDevices = async () => {
    try {
      // カメラを取得する。ブラウザのパーミッション確認もこの段階で行われる
      cameras.value = await repository.getCameras();
      cameraLabels.value = cameras.value.map(item => item.label);
      if (cameras.value.length > 0) {
        // カメラが複数台、かつ未選択状態の場合は、OBS studioのバーチャルカメラを選択状態とする
        if (selectedCamera.value === undefined) {
          const obsCamera = cameras.value.find(item => item.label.toLowerCase().includes("obs"));
          if (obsCamera) {
            selectedCamera.value = obsCamera;
          } else {
            selectedCamera.value = cameras.value[0];
          }
        }
      } else {
        selectedCamera.value = undefined;
      }
    } catch (error: any) {
      videoStateStore.setError("カメラが使用できません。\n設定でカメラの使用を許可してください。");
      return;
    }
    try {
      // マイクを取得する。ブラウザのパーミッション確認もこの段階で行われる
      microphones.value = await repository.getMicrophones();
      microphoneLabels.value = microphones.value.map(item => item.label);
      if (microphones.value.length > 0) {
        // マイクが複数台、かつ未選択状態の場合は、最初のカメラを選択状態とする
        if (selectedMicrophone.value === undefined) {
          selectedMicrophone.value = microphones.value[0];
        }
      } else {
        selectedMicrophone.value = undefined;
      }
      fetchedDevices.value = true;
    } catch (error: any) {
      videoStateStore.setError("マイクが使用できません。\n設定でマイクの使用を許可してください。");
    }
  };

  /**
   * デバイスの変更コールバックを設定する
   */
  const setCallBack = () => {
    repository.setCallBack(() => {
      fetchDevices();
    });
  };

  /**
   * デバイスの変更コールバックを削除
   */
  const removeCallBack = () => {
    repository.setCallBack(undefined);
  };

  /**
   * device.labelを指定して、カメラを選択する
   * @param label
   */
  const selectCameraByLabel = (label: string) => {
    const findItem = cameras.value.find(item => item.label === label);
    if (findItem) {
      selectedCamera.value = findItem;
    }
  };

  /**
   * device.labelを指定して、マイクを選択する
   * @param label
   */
  const selectMicrophoneByLabel = (label: string) => {
    const findItem = microphones.value.find(item => item.label === label);
    if (findItem) {
      selectedMicrophone.value = findItem;
    }
  };

  /**
   * 選択しているカメラの名称
   */
  const selectedCameraLabel = computed<string>(() => {
    if (selectedCamera.value) {
      return selectedCamera.value?.label;
    }
    return "";
  });

  /**
   * 選択しているマイクの名称
   */
  const selectedMicrophoneLabel = computed<string>(() => {
    if (selectedMicrophone.value) {
      return selectedMicrophone.value?.label;
    }
    return "";
  });

  return {
    fetchedDevices,
    selectedCamera,
    selectedCameraLabel,
    selectCameraByLabel,
    cameras,
    cameraLabels,
    selectedMicrophone,
    selectedMicrophoneLabel,
    selectMicrophoneByLabel,
    microphones,
    microphoneLabels,
    fetchDevices,
    setCallBack,
    removeCallBack,
  };
});
